import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Lock,
  Loader2,
  LogOut,
  RefreshCw,
  Trash2,
  ArrowLeft,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { API } from "@/lib/site";

const TOKEN_KEY = "spd_admin_token";

const STATUS_STYLES = {
  new: "border-gold/40 bg-gold/10 text-gold",
  contacted: "border-white/20 bg-white/10 text-white/80",
  completed: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState(null);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const fetchBookings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/bookings`, authHeaders());
      setBookings(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } else {
        toast.error("Could not load bookings");
        setBookings([]);
      }
    }
  }, [authHeaders]);

  useEffect(() => {
    if (token) fetchBookings();
  }, [token, fetchBookings]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === "string"
          ? err.response.data.detail
          : "Login failed"
      );
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setBookings(null);
  };

  const updateStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/bookings/${id}`,
        { status },
        authHeaders()
      );
      setBookings((bs) => bs.map((b) => (b.id === id ? data : b)));
    } catch {
      toast.error("Could not update status");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, authHeaders());
      setBookings((bs) => bs.filter((b) => b.id !== id));
      toast.success("Booking deleted");
    } catch {
      toast.error("Could not delete booking");
    }
  };

  if (!token) {
    return (
      <div className="grid min-h-screen place-items-center bg-ink px-5 font-body">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#0c0c0c] p-9"
        >
          <div className="grid h-12 w-12 place-items-center rounded-2xl border border-gold/30 bg-gold/10 text-gold">
            <Lock className="h-5 w-5" />
          </div>
          <h1 className="mt-6 font-heading text-2xl font-extrabold tracking-tight text-white">
            Admin Login
          </h1>
          <p className="mt-1.5 text-sm text-white/45">
            Syed's Pro Drive Inspection — bookings dashboard
          </p>
          <Input
            data-testid="admin-password-input"
            type="password"
            required
            placeholder="Admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-7 border-white/10 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-gold focus-visible:ring-1 focus-visible:ring-offset-0"
          />
          {error && (
            <p data-testid="admin-login-error" className="mt-3 text-sm font-semibold text-red-400">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3 font-heading text-sm font-extrabold text-black transition-[background-color] duration-300 hover:bg-gold-light disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Sign In
          </button>
          <Link
            data-testid="admin-back-to-site-link"
            to="/"
            className="mt-5 flex items-center justify-center gap-2 text-xs font-semibold text-white/40 transition-colors duration-300 hover:text-gold"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to site
          </Link>
        </form>
      </div>
    );
  }

  const counts = bookings
    ? {
        total: bookings.length,
        new: bookings.filter((b) => b.status === "new").length,
        contacted: bookings.filter((b) => b.status === "contacted").length,
        completed: bookings.filter((b) => b.status === "completed").length,
      }
    : null;

  return (
    <div className="min-h-screen bg-ink px-5 py-10 font-body md:px-10">
      <div className="mx-auto max-w-6xl">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src="/assets/logo-emblem.png"
              alt="Syed's Pro Drive Inspection logo"
              className="h-10 w-10 rounded-xl border border-gold/30 object-cover"
            />
            <div>
              <h1 className="font-heading text-xl font-extrabold tracking-tight text-white">
                Bookings Dashboard
              </h1>
              <p className="text-xs text-white/40">
                Syed's Pro Drive Inspection
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-btn"
              onClick={fetchBookings}
              className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white/70 transition-colors duration-300 hover:border-gold/50 hover:text-gold"
              aria-label="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <Link
              data-testid="admin-view-site-link"
              to="/"
              className="rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white/70 transition-colors duration-300 hover:border-gold/50 hover:text-gold"
            >
              View Site
            </Link>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white/70 transition-colors duration-300 hover:border-red-400/50 hover:text-red-400"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </header>

        {counts && (
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
            {[
              ["Total", counts.total, "total"],
              ["New", counts.new, "new"],
              ["Contacted", counts.contacted, "contacted"],
              ["Completed", counts.completed, "completed"],
            ].map(([label, value, slug]) => (
              <div
                key={slug}
                data-testid={`admin-stat-${slug}`}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-5"
              >
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/40">
                  {label}
                </span>
                <span className="mt-2 block font-heading text-3xl font-extrabold tracking-tighter text-white">
                  {value}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 overflow-x-auto rounded-2xl border border-white/10">
          {!bookings ? (
            <div
              data-testid="admin-bookings-loading"
              className="flex items-center justify-center gap-3 py-20 text-white/50"
            >
              <Loader2 className="h-5 w-5 animate-spin" /> Loading bookings...
            </div>
          ) : bookings.length === 0 ? (
            <div
              data-testid="admin-bookings-empty"
              className="py-20 text-center text-sm text-white/40"
            >
              No bookings yet. New requests from the website will appear here.
            </div>
          ) : (
            <table data-testid="admin-bookings-table" className="w-full min-w-[820px] text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-white/[0.03] text-xs font-bold uppercase tracking-[0.15em] text-white/40">
                  <th className="px-5 py-4">Customer</th>
                  <th className="px-5 py-4">Phone</th>
                  <th className="px-5 py-4">Car</th>
                  <th className="px-5 py-4">Service</th>
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Booked</th>
                  <th className="px-5 py-4" />
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr
                    key={b.id}
                    data-testid={`admin-booking-row-${b.id}`}
                    className="border-b border-white/5 text-white/75 transition-colors duration-300 hover:bg-white/[0.03]"
                  >
                    <td className="px-5 py-4 font-heading font-bold text-white">
                      {b.name}
                      {b.message && (
                        <span className="block max-w-[180px] truncate text-xs font-normal text-white/40">
                          {b.message}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <a href={`tel:${b.phone}`} className="hover:text-gold">
                        {b.phone}
                      </a>
                    </td>
                    <td className="px-5 py-4">{b.car_model}</td>
                    <td className="max-w-[200px] px-5 py-4">{b.service_type}</td>
                    <td className="px-5 py-4">{b.preferred_date}</td>
                    <td className="px-5 py-4">
                      <Select
                        value={b.status}
                        onValueChange={(v) => updateStatus(b.id, v)}
                      >
                        <SelectTrigger
                          data-testid={`admin-status-select-${b.id}`}
                          className={`h-8 w-32 rounded-full border text-xs font-bold ${STATUS_STYLES[b.status]}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="border-white/10 bg-[#141414] text-white">
                          {["new", "contacted", "completed"].map((s) => (
                            <SelectItem
                              key={s}
                              value={s}
                              data-testid={`admin-status-option-${s}`}
                              className="capitalize focus:bg-white/10"
                            >
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-5 py-4 text-xs text-white/40">
                      {new Date(b.created_at).toLocaleString("en-IN", {
                        day: "numeric",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="px-5 py-4">
                      <button
                        data-testid={`admin-delete-${b.id}`}
                        onClick={() => remove(b.id)}
                        aria-label="Delete booking"
                        className="grid h-8 w-8 place-items-center rounded-full border border-white/10 text-white/40 transition-colors duration-300 hover:border-red-400/50 hover:text-red-400"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
